/**
 * @file Determines if items need to be placed in an overflow container (execeed container width)
 * Based on https://sfdc.co/uOYl6
 */

import { WireAdapter, ShadowRootTheGoodPart } from "lwc";
import debounce from "lodash/debounce";

export const WIDTH_PROPERTY = Symbol("width");

interface OverflowMenuAdapterItem {
  active: boolean;
  value: string;
  [WIDTH_PROPERTY]: number;
}

type OverflowMenuAdapterDirection = "ltr" | "rtl";

export interface OverflowMenuAdapterConfig<T extends OverflowMenuAdapterItem> {
  items: T[];
  template: ShadowRootTheGoodPart;
  willUpdate?: () => void;
  direction?: OverflowMenuAdapterDirection;
}

export interface OverflowMenuAdapterValue {
  visibleItems: OverflowMenuAdapterItem[];
  overflowItems: OverflowMenuAdapterItem[];
  isMenuHidden: boolean;
}

export const OVERFLOW_MENU_ADAPTER_DEFAULT_VALUE: OverflowMenuAdapterValue = {
  visibleItems: [],
  overflowItems: [],
  isMenuHidden: true,
};

/**
 * Get the full outer width of an element
 */
export function getElementWidth(element: HTMLElement) {
  const style = getComputedStyle(element);

  return [
    "margin-left",
    "margin-right",
    "padding-left",
    "padding-right",
    "width",
  ].reduce(
    (width, prop) =>
      Math.ceil(width + parseFloat(style.getPropertyValue(prop) || "0")),
    0
  );
}

/**
 * Sort items based on whether or not they should be visible or in an overflow
 */
export function sortItems<T extends OverflowMenuAdapterItem>(
  items: T[],
  containerWidth: number = 0,
  overflowWidth: number = 0,
  direction: OverflowMenuAdapterDirection = "ltr"
) {
  const visibleItems: T[] = [];
  const overflowItems: T[] = [];
  const shouldReverse = direction === "rtl";

  const allItemsWidth = items.reduce(
    (totalWidth, item) => totalWidth + item[WIDTH_PROPERTY],
    0
  );

  // If total items width is less than containerwidth or if the containerwidth is
  // less than zero in cases where container is not yet rendered and we subtract the threshold
  // return all items as visibleItems and overflowItems empty
  if (allItemsWidth <= containerWidth || containerWidth <= 0) {
    return { visibleItems: items, overflowItems };
  }

  // Not all items fit, an overflow is needed
  let totalWidth = overflowWidth;

  // Find the active item if it exists
  const activeItem = items.find((i) => i.active);

  // The active item is always shown, so reserve space for it
  if (activeItem) {
    totalWidth += activeItem[WIDTH_PROPERTY];
  }

  let activeItemFitsWithoutRearrangement = false;

  (shouldReverse ? [...items].reverse() : items).forEach((item) => {
    if (item.active) {
      activeItemFitsWithoutRearrangement = overflowItems.length === 0;
      if (activeItemFitsWithoutRearrangement) {
        visibleItems.push(item);
      }
    } else {
      const itemFits = item[WIDTH_PROPERTY] + totalWidth <= containerWidth;
      if (itemFits && overflowItems.length === 0) {
        totalWidth += item[WIDTH_PROPERTY];
        visibleItems.push(item);
      } else {
        overflowItems.push(item);
      }
    }
  });

  // Place the active item at the end
  if (activeItem && !activeItemFitsWithoutRearrangement) {
    visibleItems.push(activeItem);
  }

  return {
    visibleItems: shouldReverse ? [...visibleItems].reverse() : visibleItems,
    overflowItems: shouldReverse ? [...overflowItems].reverse() : overflowItems,
  };
}

export class OverflowMenuAdapter implements WireAdapter {
  private config: OverflowMenuAdapterConfig<OverflowMenuAdapterItem> | null =
    null;
  private observer: ResizeObserver | null = null;
  private containerWidth: number = 0;

  private visibleItems: OverflowMenuAdapterItem[] = [];
  private overflowItems: OverflowMenuAdapterItem[] = [];

  private setValue: (value: OverflowMenuAdapterValue) => void;

  constructor(setValue: (value: OverflowMenuAdapterValue) => void) {
    this.setValue = setValue;
  }

  connect() {}

  disconnect() {
    this.observer?.disconnect();
  }

  update(config: OverflowMenuAdapterConfig<OverflowMenuAdapterItem>) {
    this.config = config;

    // Listen for container resize
    if (!this.observer) {
      this.observer = new ResizeObserver(
        debounce((entries: ResizeObserverEntry[]) => {
          this.containerWidth = entries[0].contentRect?.width;
          this.detectOverflow();
        }, 250)
      );

      this.observer?.observe(
        this.config.template.querySelector('[data-overflow="container"]')!
      );
    }

    // Determine if the next active item is already visible
    const activeItemValue = this.config.items.find((i) => i.active)?.value;
    const shouldDetect = !this.visibleItems.find(
      (i) => i.value === activeItemValue
    );

    if (shouldDetect) {
      // Calculate item overflow
      this.detectOverflow();
    } else {
      // Update items without re-calculating overflow
      this.setValue({
        visibleItems: this.visibleItems.map((i) => ({
          ...i,
          active: i.value === activeItemValue,
        })),
        overflowItems: this.overflowItems,
        isMenuHidden: !this.overflowItems.length,
      });
    }
  }

  private detectOverflow() {
    if (!this.config) return;

    this.config?.willUpdate?.();

    const { items, template } = this.config;

    const overflowMenuNode = template.querySelector(
      '[data-overflow="overflow"]'
    );

    // Get the item DOM nodes
    const overflowItemNodes = Array.from(
      template.querySelectorAll("[data-overflow-item]")
    ) as HTMLElement[];

    // Get each item's current width
    overflowItemNodes.forEach((e) => {
      const value = e.getAttribute("data-overflow-item");
      const item = this.config?.items.find((t) => t.value === value);
      if (item) {
        item[WIDTH_PROPERTY] = getElementWidth(e);
      }
    });

    // Calculate which items need to be placed in overflow
    const { visibleItems, overflowItems } = sortItems(
      items,
      this.containerWidth,
      overflowMenuNode?.getBoundingClientRect().width,
      this.config?.direction
    );

    // Keep track of items
    this.visibleItems = visibleItems;
    this.overflowItems = overflowItems;

    // Update state
    this.setValue({
      visibleItems,
      overflowItems,
      isMenuHidden: !overflowItems.length,
    });
  }
}
